import { useEffect, useRef } from "react";
import { useController } from "react-hook-form";
import { Col, Row, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { validateEmbedUrl } from "../../services/common-service";
import {
  toEmbedUrlSync,
  isWistiaShareLink,
  resolveWistiaShareLink
} from "../../services/video-embed";
import ValidationMessage from "../validation-message/validation-message";

// Wait for the user to stop typing before asking Wistia about a share link
export const SHARE_LINK_LOOKUP_DELAY_MS = 400;

const VideoLinkWithPreview = ({
  id,
  name,
  error,
  control,
  isDisabled,
  setIsValid,
  showPreview,
  setIsValidating,
  videoLink
}) => {
  const { t } = useTranslation();
  const { field } = useController({ name, control });
  const { value } = field;
  const onChangeRef = useRef(field.onChange);
  onChangeRef.current = field.onChange;

  // A Wistia share link in the field is resolved whenever it gets there:
  // typed or pasted, but also from a saved draft or a form reset.
  useEffect(() => {
    if (!isWistiaShareLink(value)) return undefined;
    let cancelled = false;
    let lookupStarted = false;
    setIsValid(false);
    const timer = setTimeout(async () => {
      lookupStarted = true;
      setIsValidating(true);
      const embedUrl = await resolveWistiaShareLink(value);
      if (cancelled) return; // newer value, other section or unmounted
      setIsValidating(false);
      if (embedUrl) {
        setIsValid(validateEmbedUrl(embedUrl));
        onChangeRef.current(embedUrl);
      }
    }, SHARE_LINK_LOOKUP_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (lookupStarted) setIsValidating(false);
    };
  }, [value, name, setIsValid, setIsValidating]);

  const handleOnChange = (input) => {
    // Share links stay as typed until the effect above has resolved them
    if (isWistiaShareLink(input)) {
      field.onChange(input);
      return;
    }
    const embedUrl = toEmbedUrlSync(input);
    if (input) setIsValid(Boolean(embedUrl) && validateEmbedUrl(embedUrl));
    field.onChange(embedUrl || input);
  };

  return (
    <Row className="mt-3">
      <Col sm={12} md={6}>
        <Form.Group controlId={`videoLink-${id}`}>
          <Form.Label className={error && "text-danger"}>
            {t("customerWebsite.videoLink")}
            {error && "*"}
          </Form.Label>
          <Form.Control
            type="text"
            value={value ?? ""}
            disabled={isDisabled}
            placeholder={t("customerWebsite.videoLinkPlaceholder")}
            onChange={({ target }) => handleOnChange(target.value)}
          />
          <Form.Text muted>{t("customerWebsite.videoLinkHelp")}</Form.Text>
          {error && <ValidationMessage error={error.message} />}
        </Form.Group>
      </Col>
      {showPreview && !isWistiaShareLink(videoLink) && (
        <Col sm={12} md={6}>
          <iframe
            title="Video preview"
            className="w-100 d-block"
            src={videoLink}
            allowFullScreen
          />
        </Col>
      )}
    </Row>
  );
};
export default VideoLinkWithPreview;
